import { api } from "../../api/client";
import { auth, appCheck } from "../../firebase.config";
import { getToken } from "firebase/app-check";
import type { ThreatTo } from "../../wizard/ThreatStep";

// Kind matches the API's `collect_filtered` call sites in routes/reports.py.
// Cases, intel and POI are clickable into a detail view; performance and
// popularity reports don't have one yet, so they render with a stub link.
export type ThreatItemKind = "case" | "intel" | "poi" | "performance" | "popularity";

export interface ThreatItem {
  id: string;
  kind: ThreatItemKind;
  title: string;
  threatTo: ThreatTo;
  threatTarget: string | null;
  threatDetails: string | null;
  classification: string | null;
  severity: string | null;
  branchId: string | null;
  wardId: string | null;
  constituencyId: string | null;
  createdAt: string | null;
}

export interface ThreatsReport {
  items: ThreatItem[];
  byTarget: Record<string, number>;
}

export interface CaseSummary {
  total: number;
  byStatus: Record<string, number>;
  byClassification: Record<string, number>;
  byBranch: Record<string, number>;
}

export interface CaseTrendPoint {
  date: string;
  count: number;
}

export interface AuditEntry {
  id: string;
  actorUid: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetId: string;
  before: unknown;
  after: unknown;
  ip: string;
  userAgent: string;
  at: string;
}

interface AuditList { items: AuditEntry[]; }

export const ReportsApi = {
  caseSummary: () => api<CaseSummary>("/reports/case-summary"),
  caseTrend: () => api<{ series: CaseTrendPoint[] }>("/reports/case-trend"),
  threats: () => api<ThreatsReport>("/reports/threats"),
  auditLog: (filters?: { actor?: string; action?: string; targetType?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (filters?.actor) qs.set("actor", filters.actor);
    if (filters?.action) qs.set("action", filters.action);
    if (filters?.targetType) qs.set("targetType", filters.targetType);
    if (filters?.limit) qs.set("limit", String(filters.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<AuditList>(`/reports/audit-log${suffix}`);
  },

  // Export uses fetch directly so we can stream the CSV blob to a download
  // — the JSON-decoding `api()` helper would corrupt it.
  exportCasesCsv: async (): Promise<{ filename: string; blob: Blob }> => {
    const base = (import.meta.env.VITE_API_BASE_URL || "") + "/api/v1";
    const headers: Record<string, string> = {};
    const user = auth.currentUser;
    if (user) headers["Authorization"] = `Bearer ${await user.getIdToken()}`;
    if (appCheck) {
      try {
        headers["X-Firebase-AppCheck"] = (await getToken(appCheck)).token;
      } catch { /* App Check optional in dev */ }
    }
    const res = await fetch(`${base}/reports/cases/export`, { headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Export failed: ${res.status}`);
    }
    const cd = res.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const filename = m?.[1] || "cases.csv";
    return { filename, blob: await res.blob() };
  },
};
